// Hook: inject hreflang <link> tags for a given page slug.
// Only indexes languages that are indexable AND have translated content.
// Cleans up on unmount / slug change.

import { useEffect } from "react";
import { SITE_URL } from "../translations";
import { INDEXABLE_LANGS, buildLocalizedPath, hreflangCode } from "../i18nConfig";
import { SEO_CONTENT, SEO_CONTENT_I18N, TOOL_KEY_MAP } from "../seoContent";

function hasContentForLang(slug, langCode) {
  if (langCode === "en") return true;
  const toolKey = TOOL_KEY_MAP[slug];
  if (!toolKey) return false;
  if (SEO_CONTENT[toolKey]?.[langCode]) return true;
  if (SEO_CONTENT_I18N?.[toolKey]?.[langCode]) return true;
  return false;
}

// slugType: "tool" | "category" | "home"
export function useHreflang(slug, slugType = "tool") {
  useEffect(() => {
    const cleanup = () => {
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    };

    // Determine which indexable langs have real content for this page
    let langsWithContent;
    if (slugType === "home" || slugType === "category") {
      // Home and category pages are translated for all indexable langs
      langsWithContent = INDEXABLE_LANGS;
    } else {
      // Tool pages: check SEO_CONTENT
      langsWithContent = INDEXABLE_LANGS.filter((l) => hasContentForLang(slug, l.code));
    }

    // Only inject hreflang if 2+ languages cover this page
    if (langsWithContent.length < 2) {
      cleanup();
      return cleanup;
    }

    cleanup(); // Remove any stale tags first

    langsWithContent.forEach((l) => {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = hreflangCode(l.code);
      link.href = `${SITE_URL}${buildLocalizedPath(slug || "", l.code)}`;
      document.head.appendChild(link);
    });

    // x-default always points to the EN version
    const xDefault = document.createElement("link");
    xDefault.rel = "alternate";
    xDefault.hreflang = "x-default";
    xDefault.href = `${SITE_URL}${buildLocalizedPath(slug || "", "en")}`;
    document.head.appendChild(xDefault);

    return cleanup;
  }, [slug, slugType]);
}
